import { createSvgEl } from '@learnforge/demo-render';

export interface TransformationAnnotationOptions {
  base: string;
  a: number;
  b: number;
  h: number;
  k: number;
  xDomain: [number, number];
  yDomain: [number, number];
  width: number;
  height: number;
}

export interface TransformationAnnotationsHandle {
  update(options: TransformationAnnotationOptions): void;
  destroy(): void;
}

function formatNumber(value: number): string {
  if (Math.abs(value) < 1e-12) return '0';
  return String(Number(value.toFixed(2))).replace('-', '−');
}

function formatPeriod(period: number): string {
  const ratio = period / Math.PI;
  if (Math.abs(ratio - 2) < 1e-10) return '2π';
  if (Math.abs(ratio - 1) < 1e-10) return 'π';
  if (Math.abs(ratio - 0.5) < 1e-10) return 'π/2';
  return formatNumber(period);
}

export function createTransformationAnnotations(
  parent: SVGGElement,
): TransformationAnnotationsHandle {
  const root = createSvgEl('g', { class: 'fn-transform-annotations' });
  parent.appendChild(root);

  function render(options: TransformationAnnotationOptions): void {
    root.replaceChildren();
    const periodic =
      options.base === 'sin(x)' || options.base === 'cos(x)' || options.base === 'tan(x)';
    if (!periodic || options.b === 0) return;

    const toX = (x: number): number =>
      ((x - options.xDomain[0]) / (options.xDomain[1] - options.xDomain[0])) * options.width;
    const toY = (y: number): number =>
      options.height -
      ((y - options.yDomain[0]) / (options.yDomain[1] - options.yDomain[0])) * options.height;
    const clamp = (value: number, min: number, max: number): number =>
      Math.min(max, Math.max(min, value));
    const line = (className: string, x1: number, y1: number, x2: number, y2: number): void => {
      root.appendChild(createSvgEl('line', { class: className, x1, y1, x2, y2 }));
    };
    const label = (
      text: string,
      x: number,
      y: number,
      anchor: 'start' | 'middle' | 'end' = 'middle',
    ): void => {
      const node = createSvgEl('text', {
        class: 'fn-transform-annotation-label',
        x,
        y,
        'text-anchor': anchor,
      });
      node.textContent = text;
      root.appendChild(node);
    };

    const bounded = options.base !== 'tan(x)';
    const amplitude = Math.abs(options.a);
    const period = (options.base === 'tan(x)' ? Math.PI : 2 * Math.PI) / Math.abs(options.b);
    const cap = 6;

    if (bounded) {
      const lower = options.k - amplitude;
      const upper = options.k + amplitude;
      line('fn-transform-envelope', 0, toY(lower), options.width, toY(lower));
      line('fn-transform-envelope', 0, toY(upper), options.width, toY(upper));
      if (options.k !== 0) {
        line('fn-transform-midline', 0, toY(options.k), options.width, toY(options.k));
      }

      const peakX = options.base === 'sin(x)' ? options.h + period / 4 : options.h;
      const amplitudeX = clamp(toX(peakX), 24, options.width - 80);
      const amplitudeStart = toY(options.k);
      const amplitudeEnd = toY(options.k + options.a);
      line('fn-transform-measurement', amplitudeX, amplitudeStart, amplitudeX, amplitudeEnd);
      line(
        'fn-transform-measurement',
        amplitudeX - cap,
        amplitudeEnd,
        amplitudeX + cap,
        amplitudeEnd,
      );
      label(
        `A = ${formatNumber(amplitude)}`,
        amplitudeX + 9,
        (amplitudeStart + amplitudeEnd) / 2,
        'start',
      );
    }

    const periodY = clamp(
      toY(bounded ? options.k + amplitude : options.k) - 18,
      28,
      options.height - 12,
    );
    const firstCycle = Math.ceil((options.xDomain[0] - options.h) / period);
    const lastCycle = Math.floor((options.xDomain[1] - options.h - period) / period);
    if (firstCycle <= lastCycle) {
      const centeredCycle = Math.round(
        ((options.xDomain[0] + options.xDomain[1]) / 2 - options.h - period / 2) / period,
      );
      const cycle = clamp(centeredCycle, firstCycle, lastCycle);
      const periodStart = toX(options.h + cycle * period);
      const periodEnd = toX(options.h + (cycle + 1) * period);
      line('fn-transform-measurement', periodStart, periodY, periodEnd, periodY);
      line('fn-transform-measurement', periodStart, periodY - cap, periodStart, periodY + cap);
      line('fn-transform-measurement', periodEnd, periodY - cap, periodEnd, periodY + cap);
      label(`T = ${formatPeriod(period)}`, (periodStart + periodEnd) / 2, periodY - 8);
    } else {
      label(`T = ${formatPeriod(period)}`, options.width - 16, periodY - 8, 'end');
    }

    if (options.h !== 0) {
      const shiftY = toY(options.k) + 18;
      line('fn-transform-measurement', toX(0), shiftY, toX(options.h), shiftY);
      label(`h = ${formatNumber(options.h)}`, (toX(0) + toX(options.h)) / 2, shiftY + 16);
    }
    if (options.k !== 0) {
      const shiftX = toX(options.h) - 18;
      line('fn-transform-measurement', shiftX, toY(0), shiftX, toY(options.k));
      label(`k = ${formatNumber(options.k)}`, shiftX - 8, (toY(0) + toY(options.k)) / 2);
    }
  }

  return {
    update: render,
    destroy() {
      root.remove();
    },
  };
}
